import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { Connector } from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { CallbackSnippet, IdentitySecret, WebEmbedSnippet } from "./snippets";

interface WebChannelFieldsProps {
  mode: "create" | "edit";
  connector?: Connector;
  clientId?: string;
  allowedOrigins: string;
  onAllowedOriginsChange: (value: string) => void;
  webCallEnabled: boolean;
  onWebCallEnabledChange: (value: boolean) => void;
  publicCallbackEnabled: boolean;
  onPublicCallbackEnabledChange: (value: boolean) => void;
}

/**
 * The chat-on-your-site channel: who may embed it, what the widget is allowed
 * to do, and — once the connector exists — the snippets to paste.
 */
export const WebChannelFields = ({
  mode,
  connector,
  clientId,
  allowedOrigins,
  onAllowedOriginsChange,
  webCallEnabled,
  onWebCallEnabledChange,
  publicCallbackEnabled,
  onPublicCallbackEnabledChange,
}: WebChannelFieldsProps) => {
  const { t } = useTranslation("connectors");

  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="allowed-origins">
          {t("connectors.form.allowedOrigins", "Domaines autorisés")}
        </Label>
        <Input
          id="allowed-origins"
          value={allowedOrigins}
          onChange={(e) => onAllowedOriginsChange(e.target.value)}
          placeholder="example.com, www.example.com"
        />
        <p className="text-xs text-muted-foreground">
          {t(
            "connectors.form.allowedOriginsHint",
            "Séparés par des virgules. Vide = tous les domaines. Les sous-domaines sont inclus. Contrôle best-effort (le verrou fort reste le CSP frame-ancestors).",
          )}
        </p>
      </div>

      <div className="flex items-start gap-3 rounded-lg border p-4">
        <Checkbox
          id="web-call-enabled"
          checked={webCallEnabled}
          onCheckedChange={(checked) =>
            onWebCallEnabledChange(checked === true)
          }
        />
        <div className="space-y-1">
          <Label htmlFor="web-call-enabled" className="text-sm font-semibold">
            {t("connectors.form.webCallTitle")}
          </Label>
          <p className="text-xs text-muted-foreground">
            {t("connectors.form.webCallDescription")}
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg border p-4">
        <Checkbox
          id="public-callback-enabled"
          checked={publicCallbackEnabled}
          onCheckedChange={(checked) =>
            onPublicCallbackEnabledChange(checked === true)
          }
        />
        <div className="space-y-1">
          <Label
            htmlFor="public-callback-enabled"
            className="text-sm font-semibold"
          >
            {t("connectors.form.publicCallbackTitle")}
          </Label>
          {/* Said plainly, because the identifier above is already public: it is
              in the source of every page carrying the widget. Turning this on is
              what promotes it from a chat key into a way of making this line dial a
              number, so the tenant should see the door before opening it. */}
          <p className="text-xs text-muted-foreground">
            {t("connectors.form.publicCallbackDescription")}
          </p>
        </div>
      </div>

      {mode === "edit" && connector?.id ? (
        <>
          <WebEmbedSnippet connectorId={connector.id} clientId={clientId} />
          {publicCallbackEnabled && (
            <CallbackSnippet connectorId={connector.id} />
          )}
          {connector.configuration?.identity_secret && (
            <IdentitySecret secret={connector.configuration.identity_secret} />
          )}
        </>
      ) : (
        <div className="rounded-lg border bg-muted/50 p-4 text-sm text-muted-foreground">
          {t(
            "connectors.form.webCreateHint",
            "Créez le connecteur, puis rouvrez-le pour copier le script d'installation.",
          )}
        </div>
      )}
    </>
  );
};
